import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Button, Field, Groupper, Header, Input, Message, Toolbar, Modal, Dropdown, Checkbox, useFrontier } from "react-frontier2";
import { SetLoading } from "@arema/components/Classes";
import { PaymentTerminal, PaymentTerminalLicense, PaymentTerminalTypes } from '../../AdminClasses';
import { bindClick, bindFormChange } from "@arema/components/Util";
import { DeletedBanner, ErrorHeader, NotFound, UserLog } from "../../components";
import { useUser } from "../../AdminHooks";
import { QRCodeSVG } from "qrcode.react";
import { useTitle } from "@arema/components/Hooks";
import { useTranslation } from "react-i18next";
import { TERMINAL_TYPES } from '../../AdminUtil';
import API from "../../API";
import Validator from "@arema/components/Validator";
import UserAccess from "../../UserAccess";

enum Modals {
	REGISTER_TOKEN = 1,
	RESET_TOKEN = 2,
	EDIT_LICENSE = 3,
	EDIT_SERIAL = 4,
	DELETE_TERMINAL = 100,
}

var PaymentTerminalView = ()=>{
	var params = useParams();
	var { t } = useTranslation();
	var { user, hasAccess } = useUser()
	var { setTitle } = useTitle(); 
	var { Toast } = useFrontier();
	var [terminal, setTerminal] = useState<PaymentTerminal>(null);
	var [editForm, setEditForm] = useState<PaymentTerminal>(null);
	var [errorPrompts, setErrorPrompts] = useState<string[]>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [shownModal, setShownModal] = useState<Modals>(null);
	var [modalConfirm, setModalConfirm] = useState<boolean>(false);
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);
	var [licenses, setLicenses] = useState<PaymentTerminalLicense[]>(null);
	var terminal_id = params?.terminal_id && !Number.isNaN(parseInt(params.terminal_id)) ? parseInt(params.terminal_id) : null;

	var loadData = async (terminal_id: number)=>{
		API.getPaymentTerminal(terminal_id).then(res=>{
			if(res.error) return setLoadError(res.message);
			setTitle(`Terminal #${res.data.terminal_id}`);
			setTerminal(res.data);
		}).catch(err=>{
			setLoadError('Hubo un error cargando la terminal (LCL-PYTRMVW-1)');
		});
	}

	useEffect(()=>{
		if(terminal_id){
			loadData(terminal_id);
		}
	}, []);

	useEffect(()=>{
		setEditForm({
			...terminal
		});
		setModalPrompts(null);
	}, [shownModal]);

	var submit = async (setLoading: SetLoading)=>{
		var { valid, prompts } = Validator(terminal, {
			terminal_name: [{ rule: 'length', params: [4, 32], label: 'Nombre' }],
			terminal_type: [{ rule: 'number', label: 'Tipo' }],
		});
		setErrorPrompts(prompts);
		if(!valid) return;

		setLoading(true);
		API.editPaymentTerminal(terminal_id, terminal.terminal_name, terminal.terminal_type).then(res=>{
			if(res.error) return setErrorPrompts([res.message]);
			Toast.success('Se ha editando la terminal');
		}).catch(err=>{
			setErrorPrompts(['Hubo un error inesperado editando la terminal (LCL-PYTRMVW-2)']);
		}).finally(()=>{
			setLoading(false);
		});
	}

	var sendReinitialize = (setLoading: SetLoading)=>{
		setLoading(true);
		API.reinitializeTerminal(terminal.terminal_id).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			setTerminal({
				...terminal,
				terminal_token: res.data.token
			});
			Toast.success('Se ha reinicializado la terminal. Se desconectaron los dispositivos activos con este token.');
			setShownModal(null);
		}).catch(err=>{
			setModalPrompts(['Hubo un error inesperado reinicializando la terminal. (LCL-PYTRMVW-3)']);
		}).finally(()=>{
			setLoading(false);
		});
	}

	var submitLicense = async (setLoading: SetLoading)=>{
		var { valid, prompts } = Validator(terminal, {
			license_id: [{ rule: 'number' }],
		});
		setModalPrompts(prompts);
		if(!valid) return;
		setLoading(true);
		API.editLicenseTerminal(terminal.terminal_id, terminal.license_id).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Se ha editado la licencia');
			setShownModal(null);
		}).catch(err=>{
			setModalPrompts(['Hubo un error inesperado creando la terminal (LCL-PYTRMVW-3)']);
		}).finally(()=>{
			setLoading(false);
		});
	}

	var showLicenseModal = ()=>{
		setShownModal(Modals.EDIT_LICENSE)
		API.getLicenses().then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			setLicenses(res.data)
		}).catch((err)=>{
			setModalPrompts(['Hubo un error inesperado cargando las licencias (LCL-PYTRMVW-4)']);
		})
	}

	var deleteTerminal = (setLoading: SetLoading)=>{
		setLoading(true);
		API.deletePaymentTerminal(terminal_id).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			setTerminal({
				...terminal,
				deleted: true,
				user_deleted: user.user_id,
				user_deleted_name: user.name,
			});
			Toast.success('Se ha eliminado la terminal');
			setShownModal(null);
		}).catch(err=>{
			setModalPrompts(['Hubo un error inesperado eliminando la terminal (LCL-PYTRMVW-5)']);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var sendSerial = (setLoading: SetLoading)=>{
		var { valid, prompts } = Validator(editForm, {
			terminal_serial: [{ rule: 'minLength', params: [8], label: 'Número de serie' }]
		});
		setModalPrompts(prompts);
		if(!valid) return;
		setLoading(true);
		API.editTerminalSerial(terminal_id, editForm.terminal_serial).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			setTerminal({
				...terminal,
				terminal_serial: editForm.terminal_serial
			});
			Toast.success('Se ha asignado el número de serie de la terminal');
		}).catch(err=>{
			setModalPrompts([t('common.unexpected_code', { code: 'LCL-PYTRV-SNDSR-1' })]);
		}).finally(()=>{
			setLoading(false);
		})
	}

	if(!terminal_id) return <NotFound />
	if(loadError) return <ErrorHeader error={loadError} />
	if(!terminal) return <Header loading text="Cargando terminal" />

	var onTerminalChange = bindFormChange(terminal, setTerminal);
	var onFormChange = bindFormChange(editForm, setEditForm);

	return <>
		{!!terminal.deleted ? <DeletedBanner date_deleted={terminal.date_deleted} /> : null}
		<Groupper title={'Editar terminal'} titleRight={`ID: ${terminal_id}`} style={{ maxWidth: 500 }} actions={!terminal.deleted && (
			<Button text="Guardar" color="black" onClick={submit} />
		)}>
			{!terminal.deleted && (
				<Toolbar fitted>
					<Toolbar.Item text="Ver token" iconName="qrcode" onClick={bindClick(setShownModal, Modals.REGISTER_TOKEN)} if={hasAccess(UserAccess.CATALOGS.TERMINALS_VIEW_TOKEN)} />
					<Toolbar.Item text="Reiniciar" iconName="refresh" onClick={bindClick(setShownModal, Modals.RESET_TOKEN)} if={hasAccess(UserAccess.CATALOGS.TERMINALS_RESET)} />
					<Toolbar.Dropdown text="Editar" iconName="pen" if={hasAccess(UserAccess.CATALOGS.TERMINALS_EDIT_CONFIG)} items={[
						{ text: "Editar licencia", onClick: showLicenseModal },
						{ text: "Editar número de serie", onClick: bindClick(setShownModal, Modals.EDIT_SERIAL) }
					]} />
				</Toolbar>
			)}
			<Input label="Nombre" placeholder="Nombre" value={terminal.terminal_name} onChange={onTerminalChange('terminal_name')} />
			<Dropdown
				value={terminal.terminal_type}
				onValueChange={onTerminalChange('terminal_type')}
				label='Tipo de terminal'
				items={TERMINAL_TYPES.map(t=>({
					key: t.id,
					value: t.id,
					text: t.name
				}))}
			/>
			{terminal.terminal_type===PaymentTerminalTypes.GETNET_API && !terminal.terminal_serial && (
				<Message type="error" header="Información requerida" centered>
					La terminal no podrá ser usada hasta que se ingrese el <b>número de serie</b> del la términal Getnet
					{hasAccess(UserAccess.CATALOGS.TERMINALS_EDIT_CONFIG) && (
						<Button text="Asignar" onClick={bindClick(setShownModal, Modals.EDIT_SERIAL)} style={{ display: 'block', width: 150, margin: 'auto', marginTop: 10 }} color="red" />
					)}
				</Message>
			)}
			<Message list={errorPrompts} type="error" style={{ marginTop: 15 }} />
		</Groupper>
		<UserLog user={terminal} />
		{!terminal.deleted && hasAccess(UserAccess.CATALOGS.TERMINALS_DELETE) && (
			<Button text="Eliminar terminal" color="red" basic style={{ display: 'block', margin: 'auto', width: 150, marginTop: 15 }} onClick={bindClick(setShownModal, Modals.DELETE_TERMINAL)} />
		)}

		{hasAccess(UserAccess.CATALOGS.TERMINALS_VIEW_TOKEN) && (
			<Modal open={shownModal === Modals.REGISTER_TOKEN} size='mini' onClose={bindClick(setShownModal)}>
				<Modal.Header>Token de registro</Modal.Header>
				<Modal.Content>
					<div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
						<div style={{ marginBottom: 10, fontWeight: 'bold', maxWidth: 300, textAlign: 'center' }}>
							Este token se usará para registrar la terminal al sistema de arema. <br />
							Solo es necesario para cuando la terminal se registra como nueva.
						</div>
						<QRCodeSVG value={`${terminal.terminal_id}x${terminal.terminal_token}`} width={300} />
						<div className='fr mono' style={{ fontSize: 28, fontWeight: 'bold', marginTop: 10 }}>
							{`${terminal.terminal_id}x${terminal.terminal_token?.toUpperCase()}`}
						</div>
						<div style={{ marginTop: 2, color: 'gray' }}>
							Auth: {user.user_id}
						</div>
					</div>
				</Modal.Content>
				<Modal.Actions>
					<Button text='Cerrar' basic onClick={bindClick(setShownModal)} />
				</Modal.Actions>
			</Modal>
		)}
		{hasAccess(UserAccess.CATALOGS.TERMINALS_RESET) && (
			<Modal open={shownModal === Modals.RESET_TOKEN} size='tiny' onClose={bindClick(setShownModal)}>
				<Modal.Header>Reinicializar Terminal de pago</Modal.Header>
				<Modal.Content>
					<Header text='¿Reinicializar Terminal?' style={{ marginBottom: 20 }} />
					<Message type='info' centered>
						Esto desvinculará esta terminal de cualquier dispositivo donde se haya registrado con este token. El dispositivo tendrá que vincularse de nuevo a un token para que pueda realizar cobros.
					</Message>
					<Checkbox checked={!!modalConfirm} onChange={bindClick(setModalConfirm, true)} label={'Deseo reinicializar la terminal'} style={{ marginTop: 20 }} />
					<Message list={modalPrompts} type='error' />
				</Modal.Content>
				<Modal.Actions>
					<Button text='Cerrar' basic onClick={bindClick(setShownModal)} />
					<Button text='Continuar' color='black' disabled={!modalConfirm} onClick={sendReinitialize} />
				</Modal.Actions>
			</Modal>
		)}
		{hasAccess(UserAccess.CATALOGS.TERMINALS_EDIT_CONFIG) && <>
			<Modal open={shownModal === Modals.EDIT_LICENSE} size='tiny' onClose={bindClick(setShownModal)}>
				<Modal.Header>Editar licencia</Modal.Header>
				<Modal.Content>
					{licenses ? (
						<Dropdown
							items={licenses.map(l=>({ text: l.license_name, value: l.license_id }))}
							value={terminal.license_id}
							onValueChange={onFormChange('license_id')}
							label='Tipo de terminal'
						/>
					) : (
						<Header loading text='Cargando licencias' />
					)}
					<Message list={modalPrompts} type='error' />
				</Modal.Content>
				<Modal.Actions>
					<Button text='Cerrar' basic onClick={bindClick(setShownModal)} />
					<Button text='Guardar' color='black' disabled={!terminal.license_id} onClick={submitLicense} />
				</Modal.Actions>
			</Modal>
			<Modal open={shownModal===Modals.EDIT_SERIAL} size="tiny" onClose={bindClick(setShownModal)}>
				<Modal.Header>Editar número de serie</Modal.Header>
				<Modal.Content>
					<Input value={editForm.terminal_serial} onChange={onFormChange('terminal_serial')} label="Número de serie" />
					<Message type="info">
						El número de serie sirve para llamar la terminal desde los servidores de Getnet. Es <b>importante</b> que
						este se asigne de manera completa ya que si no el punto de venta no podrá realizar cobros.
					</Message>
					<Message list={modalPrompts} type='error' />
				</Modal.Content>
				<Modal.Actions>
					<Button basic text="Cerrar" onClick={bindClick(setShownModal, null)} />
					<Button color="black" text="Guardar" onClick={sendSerial} />
				</Modal.Actions>
			</Modal>
		</>}
		{hasAccess(UserAccess.CATALOGS.TERMINALS_DELETE) && (
			<Modal open={shownModal === Modals.DELETE_TERMINAL} size='mini' onClose={bindClick(setShownModal)}>
				<Modal.Header>Eliminar terminal</Modal.Header>
				<Modal.Content>
					<Header text={'Eliminar terminal?'} subtext={`Eliminar la terminal con el ID ${terminal_id}`} />
					<Message type="error" centered>
						Se desconectará los dispositivos que estén asociados a este ID y ya no podrá ser usado
					</Message>
					<Message list={modalPrompts} type='error' />
				</Modal.Content>
				<Modal.Actions>
					<Button text='Cerrar' basic onClick={bindClick(setShownModal)} />
					<Button text='Eliminar' color='red' disabled={!terminal.license_id} onClick={deleteTerminal} />
				</Modal.Actions>
			</Modal>
		)}
	</>
}

export default PaymentTerminalView;